import React, { useState, useEffect, useMemo } from 'react';
import { IngredientItem, ConversionItem } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import { 
  Printer, 
  X, 
  ArrowLeft, 
  Search, 
  CheckSquare, 
  Square, 
  RotateCcw, 
  CheckCheck,
  Eye
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export type TemplateType =
  | 'GOODS_INWARD'
  | 'STOCK_TRANSFER'
  | 'SHIFT_COUNT'
  | 'WASTE_REPORT'
  | 'INVENTORY_BALANCE';

type ModalStep = 'SELECT' | 'PREVIEW';
type GroupFilter = 'ALL' | 'RAW' | 'PREPARED' | 'PRODUCT';

function normalizeVietnamese(str: string): string {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

export const PrintedTemplateModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [ingredients, setIngredients] = useState<IngredientItem[]>([]);
  const [products, setProducts] = useState<Array<{ name: string; category?: string }>>([]);
  const [conversions, setConversions] = useState<ConversionItem[]>([]);
  const [templateType, setTemplateType] = useState<TemplateType>('SHIFT_COUNT');
  const [step, setStep] = useState<ModalStep>('SELECT');

  // Selected item IDs (keys: "ing:Name" or "prod:Name")
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('ALL');

  // Blank rows option (for handwriting extra unexpected items)
  const [includeBlankRows, setIncludeBlankRows] = useState(true);
  const [blankRowsCount, setBlankRowsCount] = useState(3);

  useEffect(() => {
    if (isOpen) {
      api.getMasterData()
        .then((res) => {
          setIngredients(res.ingredients || []);
          setProducts(res.products || []);
          setConversions(res.conversions || []);
        })
        .catch(() => {});
      // Reset to selection step on open
      setStep('SELECT');
      setSearchQuery('');
      setGroupFilter('ALL');
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (step === 'PREVIEW') {
          setStep('SELECT');
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, step, onClose]);

  // Raw and Prepared ingredients sorted
  const rawIngredients = useMemo(() => {
    return ingredients
      .filter((ing) => ing.type !== 'PREPARED')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'vi', { sensitivity: 'base' }));
  }, [ingredients]);

  const preparedIngredients = useMemo(() => {
    return ingredients
      .filter((ing) => ing.type === 'PREPARED')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'vi', { sensitivity: 'base' }));
  }, [ingredients]);

  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'vi', { sensitivity: 'base' })
    );
  }, [products]);

  // Determine available items for current template type
  const availableItems = useMemo(() => {
    const list: Array<{
      key: string;
      name: string;
      unit: string;
      category?: string;
      type: 'RAW' | 'PREPARED' | 'PRODUCT';
    }> = [];

    if (templateType === 'GOODS_INWARD' || templateType === 'STOCK_TRANSFER') {
      // Goods inward and Stock transfer (Kho Tổng -> Quầy Bar) ONLY use raw ingredients (Kho Tổng tuyệt đối không có BTP)
      // Dùng đơn vị bao bì từ 04_Quy_doi (hộp, thùng, túi, chai...)
      rawIngredients.forEach((ing) => {
        const matchedConv = conversions.find(
          (c) => c.ingredient_name.trim().toLowerCase() === ing.name.trim().toLowerCase()
        );
        list.push({
          key: `ing:${ing.name}`,
          name: ing.name,
          unit: matchedConv?.package_unit || ing.unit,
          category: ing.category,
          type: 'RAW',
        });
      });
    } else if (templateType === 'SHIFT_COUNT') {
      // Shift count has Raw, Prepared, and Finished Products
      rawIngredients.forEach((ing) => {
        list.push({
          key: `ing:${ing.name}`,
          name: ing.name,
          unit: ing.unit,
          category: ing.category,
          type: 'RAW',
        });
      });
      preparedIngredients.forEach((ing) => {
        list.push({
          key: `ing:${ing.name}`,
          name: ing.name,
          unit: ing.unit,
          category: ing.category,
          type: 'PREPARED',
        });
      });
      sortedProducts.forEach((prod) => {
        list.push({
          key: `prod:${prod.name}`,
          name: prod.name,
          unit: 'Ly',
          category: prod.category,
          type: 'PRODUCT',
        });
      });
    } else {
      // Waste, Inventory balance use Raw & Prepared
      rawIngredients.forEach((ing) => {
        list.push({
          key: `ing:${ing.name}`,
          name: ing.name,
          unit: ing.unit,
          category: ing.category,
          type: 'RAW',
        });
      });
      preparedIngredients.forEach((ing) => {
        list.push({
          key: `ing:${ing.name}`,
          name: ing.name,
          unit: ing.unit,
          category: ing.category,
          type: 'PREPARED',
        });
      });
    }

    return list;
  }, [templateType, rawIngredients, preparedIngredients, sortedProducts, conversions]);

  // Filter items by search query and group filter
  const filteredItems = useMemo(() => {
    const normalizedQuery = normalizeVietnamese(searchQuery);

    return availableItems.filter((item) => {
      // Group filter check
      if (groupFilter !== 'ALL' && item.type !== groupFilter) {
        return false;
      }

      // Search query check
      if (normalizedQuery) {
        const normName = normalizeVietnamese(item.name);
        const normCat = normalizeVietnamese(item.category || '');
        if (!normName.includes(normalizedQuery) && !normCat.includes(normalizedQuery)) {
          return false;
        }
      }

      return true;
    });
  }, [availableItems, groupFilter, searchQuery]);

  // Count items currently selected that are valid for this template
  const selectedCount = useMemo(() => {
    let count = 0;
    availableItems.forEach((item) => {
      if (selectedItems.has(item.key)) count++;
    });
    return count;
  }, [availableItems, selectedItems]);

  if (!isOpen) return null;

  const handleToggleItem = (key: string) => {
    tg.haptic.selectionChanged();
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    tg.haptic.impact('light');
    setSelectedItems((prev) => {
      const next = new Set(prev);
      filteredItems.forEach((item) => next.add(item.key));
      return next;
    });
  };

  const handleDeselectAll = () => {
    tg.haptic.impact('light');
    setSelectedItems(new Set());
  };

  const handleConfirmToPreview = () => {
    tg.haptic.impact('medium');
    setStep('PREVIEW');
  };

  const handlePrint = () => {
    tg.haptic.impact('medium');
    window.print();
  };

  const currentDate = new Date().toLocaleDateString('vi-VN');

  const getTemplateMeta = () => {
    switch (templateType) {
      case 'GOODS_INWARD':
        return {
          flow: 'L1',
          title: '[L1] PHIẾU NHẬP HÀNG KHO TỔNG',
          subtitle: 'Luồng 1: Nhập hàng từ Nhà Cung Cấp về Kho Tổng',
          actionCol: 'SL Nhập',
          extraCol: 'Ghi chú / NCC',
        };
      case 'STOCK_TRANSFER':
        return {
          flow: 'L2',
          title: '[L2] PHIẾU CHUYỂN KHO ĐẦU CA',
          subtitle: 'Luồng 2: Xuất Kho Tổng ➔ Quầy Bar',
          actionCol: 'SL Chuyển',
          extraCol: 'Ghi chú',
        };
      case 'SHIFT_COUNT':
        return {
          flow: 'L3',
          title: '[L3] PHIẾU CHỐT TỒN CA & DOANH SỐ',
          subtitle: 'Luồng 3: Cân đo tồn NL thô, BTP đã pha & Doanh số bán',
          actionCol: 'SL Cân Tồn',
          extraCol: 'Ghi chú',
        };
      case 'WASTE_REPORT':
        return {
          flow: 'L4',
          title: '[L4] PHIẾU BÁO HỦY / HỎNG HÀNG',
          subtitle: 'Luồng 4: Hủy nguyên liệu & BTP',
          actionCol: 'SL Hủy',
          extraCol: 'Lý do hủy',
        };
      case 'INVENTORY_BALANCE':
        return {
          flow: 'L5',
          title: '[L5] PHIẾU KIỂM KÊ CÂN BẰNG KHO',
          subtitle: 'Luồng 5: Kiểm kê định kỳ Kho Tổng / Quầy Bar',
          actionCol: 'SL Đếm TT',
          extraCol: 'Ghi chú',
        };
    }
  };

  const meta = getTemplateMeta();
  const isSalesTemplate = templateType === 'SHIFT_COUNT';

  // Selected items separated for SHIFT_COUNT template
  const selectedRaw = rawIngredients.filter((ing) => selectedItems.has(`ing:${ing.name}`));
  const selectedPrepared = preparedIngredients.filter((ing) => selectedItems.has(`ing:${ing.name}`));
  const selectedProds = sortedProducts.filter((prod) => selectedItems.has(`prod:${prod.name}`));

  // Selected items for standard templates (L1, L2, L4, L5)
  const selectedStandardRows = availableItems.filter((item) => selectedItems.has(item.key));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
          borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        {/* STEP 1: ITEM SELECTION & CONFIGURATION */}
        {step === 'SELECT' && (
          <div
            className="no-print"
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-surface)',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Printer size={18} color="var(--primary-accent)" />
                <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>In Mẫu Phiếu Nghiệp Vụ</h3>
              </div>
              <button onClick={onClose} className="stepper-btn" aria-label="Đóng">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                minHeight: 0,
              }}
            >
              {/* Template Type Selector */}
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                  1. Chọn mẫu phiếu cần in (Khổ cuộn K80 / A4):
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  <button
                    type="button"
                    className={`btn ${templateType === 'GOODS_INWARD' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setTemplateType('GOODS_INWARD');
                      setGroupFilter('ALL');
                      setSelectedItems((prev) => {
                        const next = new Set<string>();
                        prev.forEach((k) => {
                          if (k.startsWith('ing:')) {
                            const name = k.slice(4).toLowerCase();
                            const isBtp = preparedIngredients.some((p) => p.name.toLowerCase() === name);
                            if (!isBtp) next.add(k);
                          }
                        });
                        return next;
                      });
                    }}
                    style={{ fontSize: 11, minHeight: 38, padding: '6px 8px' }}
                  >
                    [L1] Nhập Kho Tổng
                  </button>
                  <button
                    type="button"
                    className={`btn ${templateType === 'STOCK_TRANSFER' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setTemplateType('STOCK_TRANSFER');
                      setGroupFilter('ALL');
                      setSelectedItems((prev) => {
                        const next = new Set<string>();
                        prev.forEach((k) => {
                          if (k.startsWith('ing:')) {
                            const name = k.slice(4).toLowerCase();
                            const isBtp = preparedIngredients.some((p) => p.name.toLowerCase() === name);
                            if (!isBtp) next.add(k);
                          }
                        });
                        return next;
                      });
                    }}
                    style={{ fontSize: 11, minHeight: 38, padding: '6px 8px' }}
                  >
                    [L2] Chuyển Kho Ca
                  </button>
                  <button
                    type="button"
                    className={`btn ${templateType === 'SHIFT_COUNT' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setTemplateType('SHIFT_COUNT');
                      setGroupFilter('ALL');
                    }}
                    style={{ fontSize: 11, minHeight: 38, padding: '6px 8px' }}
                  >
                    [L3] Chốt Ca & Doanh Số
                  </button>
                  <button
                    type="button"
                    className={`btn ${templateType === 'WASTE_REPORT' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setTemplateType('WASTE_REPORT');
                      setGroupFilter('ALL');
                    }}
                    style={{ fontSize: 11, minHeight: 38, padding: '6px 8px' }}
                  >
                    [L4] Báo Hủy / Hỏng
                  </button>
                  <button
                    type="button"
                    className={`btn ${templateType === 'INVENTORY_BALANCE' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setTemplateType('INVENTORY_BALANCE');
                      setGroupFilter('ALL');
                    }}
                    style={{ fontSize: 11, minHeight: 38, padding: '6px 8px' }}
                  >
                    [L5] Cân Bằng Kho
                  </button>
                </div>
              </div>

              {/* Search and Category Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Search Box */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'var(--bg-surface-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '6px 10px',
                  border: '1px solid var(--border-color)',
                  gap: 8,
                }}
              >
                <Search size={16} color="var(--text-tertiary)" />
                <input
                  type="text"
                  placeholder="Tìm kiếm nguyên liệu hoặc món..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    width: '100%',
                    fontSize: 13,
                    color: 'var(--text-primary)',
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: 2,
                      display: 'flex',
                    }}
                  >
                    <X size={15} color="var(--text-tertiary)" />
                  </button>
                )}
              </div>

              {/* Group Filter Tabs (if applicable) */}
              {templateType === 'SHIFT_COUNT' ? (
                <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 2 }}>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('ALL');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'ALL' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'ALL' ? '#fff' : 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Tất cả ({availableItems.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('RAW');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'RAW' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'RAW' ? '#fff' : 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    NL Thô ({rawIngredients.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('PREPARED');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'PREPARED' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'PREPARED' ? '#fff' : 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    BTP ({preparedIngredients.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('PRODUCT');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'PRODUCT' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'PRODUCT' ? '#fff' : 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Món POS ({sortedProducts.length})
                  </button>
                </div>
              ) : (templateType !== 'GOODS_INWARD' && templateType !== 'STOCK_TRANSFER') ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('ALL');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'ALL' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'ALL' ? '#fff' : 'var(--text-secondary)',
                    }}
                  >
                    Tất cả ({availableItems.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('RAW');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'RAW' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'RAW' ? '#fff' : 'var(--text-secondary)',
                    }}
                  >
                    NL Thô ({rawIngredients.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      tg.haptic.selectionChanged();
                      setGroupFilter('PREPARED');
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-full)',
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: groupFilter === 'PREPARED' ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                      color: groupFilter === 'PREPARED' ? '#fff' : 'var(--text-secondary)',
                    }}
                  >
                    BTP ({preparedIngredients.length})
                  </button>
                </div>
              ) : null}

              {/* Quick Action Tools Bar */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '2px 0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: selectedCount > 0 ? 'var(--color-warning-bg)' : 'var(--bg-surface-subtle)',
                      color: selectedCount > 0 ? 'var(--primary-accent)' : 'var(--text-tertiary)',
                      fontWeight: 800,
                      fontSize: 12,
                    }}
                  >
                    Đã chọn: {selectedCount}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={handleSelectAllFiltered}
                    className="btn btn-secondary"
                    style={{ minHeight: 28, padding: '4px 8px', fontSize: 11, gap: 4 }}
                  >
                    <CheckCheck size={13} /> Chọn tất cả
                  </button>
                  {selectedCount > 0 && (
                    <button
                      type="button"
                      onClick={handleDeselectAll}
                      className="btn btn-secondary"
                      style={{ minHeight: 28, padding: '4px 8px', fontSize: 11, gap: 4, color: 'var(--color-danger)' }}
                    >
                      <RotateCcw size={13} /> Bỏ chọn
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Items List */}
            <div
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-canvas)',
                padding: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {filteredItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 10px', color: 'var(--text-tertiary)', fontSize: 13 }}>
                  Không tìm thấy nguyên liệu / món nào phù hợp.
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isChecked = selectedItems.has(item.key);
                  return (
                    <div
                      key={item.key}
                      onClick={() => handleToggleItem(item.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: isChecked ? 'var(--primary-accent-subtle)' : 'var(--bg-surface)',
                        border: isChecked ? '1.5px solid var(--primary-accent)' : '1px solid var(--border-color)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        {isChecked ? (
                          <CheckSquare size={18} color="var(--primary-accent)" />
                        ) : (
                          <Square size={18} color="var(--text-tertiary)" />
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: isChecked ? 700 : 500,
                              color: isChecked ? 'var(--primary-accent)' : 'var(--text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.name}
                          </span>
                          {item.category && (
                            <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{item.category}</span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.type === 'PREPARED' && (
                          <span
                            className="badge"
                            style={{
                              fontSize: 9.5,
                              padding: '2px 5px',
                              backgroundColor: 'var(--color-warning-bg)',
                              color: 'var(--primary-accent)',
                            }}
                          >
                            BTP
                          </span>
                        )}
                        {item.type === 'PRODUCT' && (
                          <span
                            className="badge"
                            style={{
                              fontSize: 9.5,
                              padding: '2px 5px',
                              backgroundColor: '#E0E7FF',
                              color: '#4338CA',
                            }}
                          >
                            MÓN
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-secondary)',
                            minWidth: 32,
                            textAlign: 'right',
                          }}
                        >
                          {item.unit}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Extra Blank Rows Config */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface-subtle)',
                border: '1px solid var(--border-color)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={includeBlankRows}
                  onChange={(e) => setIncludeBlankRows(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--primary-accent)' }}
                />
                In kèm {blankRowsCount} dòng trống để viết tay phát sinh
              </label>
              {includeBlankRows && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {[2, 3, 5].map((cnt) => (
                    <button
                      key={cnt}
                      type="button"
                      onClick={() => setBlankRowsCount(cnt)}
                      style={{
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-color)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        background: blankRowsCount === cnt ? 'var(--primary-accent)' : 'var(--bg-surface)',
                        color: blankRowsCount === cnt ? '#fff' : 'var(--text-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      {cnt} dòng
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>

            {/* STICKY FOOTER: CONFIRM BUTTON ALWAYS VISIBLE */}
            <div
              style={{
                padding: '10px 14px',
                borderTop: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-surface)',
                boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.06)',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={handleConfirmToPreview}
                className="btn btn-primary btn-full"
                style={{ minHeight: 44, fontSize: 14, fontWeight: 700, gap: 8 }}
              >
                <Eye size={18} />
                {selectedCount > 0
                  ? `Xác nhận chọn (${selectedCount} món) ➔ Xem trước`
                  : 'Xem trước mẫu in (Tự viết tay)'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: PREVIEW & PRINT */}
        {step === 'PREVIEW' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Header with Back button */}
            <div
              className="no-print"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-surface)',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setStep('SELECT');
                }}
                className="btn-header-back"
                style={{ fontSize: 13, gap: 6 }}
              >
                <ArrowLeft size={16} /> Chọn lại danh sách
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                {meta.flow} &bull; {selectedCount} món {includeBlankRows ? `+ ${blankRowsCount} dòng trống` : ''}
              </div>
              <button onClick={onClose} className="stepper-btn" aria-label="Đóng">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Printable Preview Body */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '14px 12px',
                backgroundColor: 'var(--bg-canvas)',
                minHeight: 0,
              }}
            >
              {/* Printable Template (Khổ cuộn K80: chiều rộng 76-80mm, chiều dài cuộn liên tục) */}
              <div
                className="printable-template"
                style={{
                  border: '1px solid #000',
                  padding: '12px 8px',
                  backgroundColor: '#fff',
                  color: '#000',
                  fontFamily: 'monospace, Courier, monospace',
                  width: '100%',
                  maxWidth: '300px',
                  margin: '0 auto',
                  borderRadius: 4,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
              <div
                style={{
                  textAlign: 'center',
                  borderBottom: '2px dashed #000',
                  paddingBottom: 8,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      padding: '1px 5px',
                      border: '1.5px solid #000',
                      borderRadius: 3,
                      letterSpacing: '0.05em',
                    }}
                  >
                    MẪU {meta.flow}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700 }}>QUẦY BAR COFFEE</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 'bold', marginTop: 2 }}>
                  {meta.title}
                </div>
                <div style={{ fontSize: 9.5, color: '#333', marginTop: 1 }}>{meta.subtitle}</div>
                <div style={{ fontSize: 10.5, marginTop: 4 }}>
                  Ngày: {currentDate} | Ca: ...... | Kho: ......
                </div>
                <div style={{ fontSize: 9.5, fontStyle: 'italic', marginTop: 2 }}>
                  * Điền bút bi rõ ràng &rarr; Chụp gửi Bot Telegram ({meta.flow})
                </div>
              </div>

              {/* RENDER TABLE CONTENT ACCORDING TO TEMPLATE TYPE */}
              {isSalesTemplate ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Phần 1: Nguyên liệu thô tồn */}
                  {(selectedRaw.length > 0 || (selectedCount === 0 && includeBlankRows)) && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          padding: '3px 0',
                          borderBottom: '1px solid #000',
                          marginBottom: 4,
                        }}
                      >
                        I. CÂN ĐO NGUYÊN LIỆU THÔ TỒN (NL)
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            <th style={{ textAlign: 'left', padding: '4px 2px' }}>Tên NL Thô</th>
                            <th style={{ textAlign: 'center', width: 36, padding: '4px 2px' }}>ĐVT</th>
                            <th style={{ textAlign: 'center', width: 60, padding: '4px 2px' }}>SL Cân Tồn</th>
                            <th style={{ textAlign: 'center', width: 50, padding: '4px 2px' }}>Ghi chú</th>
                          </tr>
                          {selectedRaw.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                              <td style={{ padding: '4px 2px', fontWeight: 600 }}>{item.name}</td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', color: '#555' }}>
                                {item.unit}
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Phần 2: Bán thành phẩm tồn */}
                  {selectedPrepared.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          padding: '3px 0',
                          borderBottom: '1px solid #000',
                          marginBottom: 4,
                        }}
                      >
                        II. CÂN ĐO BÁN THÀNH PHẨM TỒN (BTP ĐÃ PHA)
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            <th style={{ textAlign: 'left', padding: '4px 2px' }}>Tên BTP</th>
                            <th style={{ textAlign: 'center', width: 36, padding: '4px 2px' }}>ĐVT</th>
                            <th style={{ textAlign: 'center', width: 60, padding: '4px 2px' }}>SL Cân Tồn</th>
                            <th style={{ textAlign: 'center', width: 50, padding: '4px 2px' }}>Ghi chú</th>
                          </tr>
                          {selectedPrepared.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                              <td style={{ padding: '4px 2px', fontWeight: 600 }}>[BTP] {item.name}</td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', color: '#555' }}>
                                {item.unit}
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Phần 3: Doanh số bán ra POS */}
                  {selectedProds.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          padding: '3px 0',
                          borderBottom: '1px solid #000',
                          marginBottom: 4,
                        }}
                      >
                        III. DOANH SỐ BÁN RA TRONG CA (BILL POS)
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            <th style={{ textAlign: 'left', padding: '4px 2px' }}>Tên Món Nước</th>
                            <th style={{ textAlign: 'center', width: 36, padding: '4px 2px' }}>ĐVT</th>
                            <th style={{ textAlign: 'center', width: 60, padding: '4px 2px' }}>SL Bán (Ly)</th>
                            <th style={{ textAlign: 'center', width: 50, padding: '4px 2px' }}>Ghi chú</th>
                          </tr>
                          {selectedProds.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                              <td style={{ padding: '4px 2px', fontWeight: 600 }}>{item.name}</td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', color: '#555' }}>Ly</td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', minHeight: 18 }}>
                                &nbsp;
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Phần Dòng Trống Viết Tay Bổ Sung */}
                  {(includeBlankRows || selectedCount === 0) && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: '3px 0',
                          borderBottom: '1px dashed #666',
                          marginBottom: 4,
                          color: '#444',
                        }}
                      >
                        {selectedCount === 0 ? 'MỤC KIỂM KÊ VIẾT TAY:' : 'BỔ SUNG PHÁT SINH VIẾT TAY:'}
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <tbody>
                          {Array.from({ length: selectedCount === 0 ? 8 : blankRowsCount }).map((_, idx) => (
                            <tr key={`blank-shift-${idx}`} style={{ borderBottom: '1px dotted #ccc' }}>
                              <td style={{ padding: '6px 2px', color: '#999', fontStyle: 'italic', width: '45%' }}>
                                ........................
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', width: 36, minHeight: 20 }}>
                                &nbsp;
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', width: 60, minHeight: 20 }}>
                                &nbsp;
                              </td>
                              <td style={{ textAlign: 'center', padding: '4px 2px', border: '1px solid #888', width: 50, minHeight: 20 }}>
                                &nbsp;
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                /* Standard table (L1, L2, L4, L5) */
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <tbody>
                    <tr style={{ borderBottom: '1.5px solid #000', fontWeight: 'bold' }}>
                      <th style={{ textAlign: 'left', padding: '4px 2px' }}>Tên Hàng</th>
                      <th style={{ textAlign: 'center', width: 36, padding: '4px 2px' }}>ĐVT</th>
                      <th style={{ textAlign: 'center', width: 56, padding: '4px 2px' }}>
                        {meta.actionCol}
                      </th>
                      {meta.extraCol && (
                        <th style={{ textAlign: 'center', width: 66, padding: '4px 2px' }}>
                          {meta.extraCol}
                        </th>
                      )}
                    </tr>

                    {selectedStandardRows.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                        <td style={{ padding: '5px 2px', fontWeight: 600 }}>
                          {item.type === 'PREPARED' ? '[BTP] ' : ''}
                          {item.name}
                        </td>
                        <td style={{ textAlign: 'center', padding: '5px 2px', color: '#555' }}>
                          {item.unit}
                        </td>
                        <td style={{ textAlign: 'center', padding: '5px 2px', border: '1px solid #888', minHeight: 20 }}>
                          &nbsp;
                        </td>
                        {meta.extraCol && (
                          <td style={{ textAlign: 'center', padding: '5px 2px', border: '1px solid #888', minHeight: 20 }}>
                            &nbsp;
                          </td>
                        )}
                      </tr>
                    ))}

                    {/* Dòng trống viết tay bổ sung */}
                    {(includeBlankRows || selectedStandardRows.length === 0) &&
                      Array.from({ length: selectedStandardRows.length === 0 ? 8 : blankRowsCount }).map((_, idx) => (
                        <tr key={`blank-std-${idx}`} style={{ borderBottom: '1px dotted #ccc' }}>
                          <td style={{ padding: '6px 2px', color: '#888', fontStyle: 'italic' }}>
                            ............................
                          </td>
                          <td style={{ textAlign: 'center', padding: '5px 2px', border: '1px solid #888', minHeight: 20 }}>
                            &nbsp;
                          </td>
                          <td style={{ textAlign: 'center', padding: '5px 2px', border: '1px solid #888', minHeight: 20 }}>
                            &nbsp;
                          </td>
                          {meta.extraCol && (
                            <td style={{ textAlign: 'center', padding: '5px 2px', border: '1px solid #888', minHeight: 20 }}>
                              &nbsp;
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}

              {/* Signatures */}
              <div
                style={{
                  marginTop: 16,
                  borderTop: '1px dashed #000',
                  paddingTop: 8,
                  fontSize: 11,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <div>Người kiểm: ....................</div>
                <div>Chủ shop: ....................</div>
              </div>
            </div>
            </div>

            {/* STICKY FOOTER: PRINT BUTTON */}
            <div
              className="no-print"
              style={{
                padding: '10px 14px',
                borderTop: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-surface)',
                boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.06)',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-primary btn-full"
                style={{ minHeight: 44, fontSize: 15, fontWeight: 800, gap: 8 }}
              >
                <Printer size={18} /> Bấm Để In Phiếu K80 / A4
              </button>
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)' }}>
                Chuẩn cuộn K80 (80mm) hoặc in A4. Có thể lưu file PDF nếu muốn.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
